import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
} from '@tanstack/react-router';
import { ApiRequestError } from '@/client/api';
import { AppShell } from '@/client/components/AppShell';
import { ErrorNotice, SkeletonList, Spinner } from '@/client/components/ui';
import { Reports } from '@/client/routes/Reports';
import { Schedule } from '@/client/routes/Schedule';
import { Settings } from '@/client/routes/Settings';
import { Setup } from '@/client/routes/Setup';
import { SignIn } from '@/client/routes/SignIn';
import { Today } from '@/client/routes/Today';
import { Volunteers } from '@/client/routes/Volunteers';
import { useResumeOnReconnect, useSession } from '@/client/store';

/**
 * Route tree. Defined in code rather than generated from the filesystem: eight
 * routes do not need a build step, and this keeps the whole shape visible in
 * one file.
 */

const rootRoute = createRootRoute({
  component: RootLayout,
});

/**
 * Decides which of the three shells the app is in — signed out, no workspace
 * yet, or running — before any screen renders.
 */
function RootLayout() {
  const session = useSession();
  useResumeOnReconnect();

  if (session.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="size-6 text-ink-faint" />
      </div>
    );
  }

  if (session.error instanceof ApiRequestError && session.error.isUnauthorized) {
    return <SignIn />;
  }

  if (session.error) {
    return (
      <div className="mx-auto max-w-md px-5 py-10">
        <ErrorNotice error={session.error} onRetry={() => void session.refetch()} />
      </div>
    );
  }

  if (session.data && !session.data.hasWorkspace) {
    return <Setup />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: Today });

const scheduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/schedule',
  component: Schedule,
});

const volunteersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/volunteers',
  component: Volunteers,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: Reports,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: Settings,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  scheduleRoute,
  volunteersRoute,
  reportsRoute,
  settingsRoute,
]);

export const router = createRouter({
  routeTree,
  // Every screen renders from the in-memory workspace, so a navigation is
  // pure render work. Preloading on intent costs nothing and hides even that.
  defaultPreload: 'intent',
  defaultPendingComponent: () => (
    <div className="px-4 py-6">
      <SkeletonList rows={4} />
    </div>
  ),
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export { RouterProvider };
